import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { User, Store } from "lucide-react";

export function AuthModal({ open, onClose, defaultMode = "login" }) {
  const { login, register, googleLogin, errMsg } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState(defaultMode);
  const [role, setRole] = useState("customer");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  React.useEffect(() => { setMode(defaultMode); }, [defaultMode, open]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let u;
      if (mode === "login") {
        u = await login(form.email, form.password);
        toast.success("Welcome back!");
      } else {
        u = await register({ ...form, role });
        toast.success("Account created!");
      }
      onClose();
      if (u?.role === "admin") navigate("/admin");
      else if (u?.role === "business_owner") navigate("/dashboard");
    } catch (err) {
      toast.error(errMsg(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md rounded-3xl p-8" data-testid="auth-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl tracking-tight">
            {mode === "login" ? "Welcome back" : "Join Voyes"}
          </DialogTitle>
          <p className="text-slate-500 text-sm">Discover local. Live local.</p>
          <DialogDescription className="sr-only">Sign in or create a Voyes account to save favorites, follow businesses, and manage your listings.</DialogDescription>
        </DialogHeader>

        {mode === "register" && (
          <div className="grid grid-cols-2 gap-3 mb-1">
            {[["customer", "Explorer", User], ["business_owner", "Business", Store]].map(([val, label, Icon]) => (
              <button
                key={val}
                type="button"
                onClick={() => setRole(val)}
                data-testid={`role-${val}`}
                className={`flex flex-col items-center gap-1 rounded-2xl border-2 py-4 transition-all ${
                  role === val ? "border-orange-500 bg-orange-50" : "border-slate-200 hover:border-slate-300"
                }`}
              >
                <Icon className="w-5 h-5" strokeWidth={1.5} />
                <span className="text-sm font-medium">{label}</span>
              </button>
            ))}
          </div>
        )}

        <form onSubmit={submit} className="space-y-4 mt-2">
          {mode === "register" && (
            <div>
              <Label>Name</Label>
              <Input data-testid="auth-name" required value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl mt-1" />
            </div>
          )}
          <div>
            <Label>Email</Label>
            <Input data-testid="auth-email" type="email" required value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-xl mt-1" />
          </div>
          <div>
            <Label>Password</Label>
            <Input data-testid="auth-password" type="password" required value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-xl mt-1" />
          </div>
          <Button type="submit" disabled={busy} data-testid="auth-submit"
            className="w-full rounded-full bg-orange-500 hover:bg-orange-600 py-6 text-base">
            {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
          </Button>
        </form>

        <div className="flex items-center gap-3 my-1">
          <div className="h-px bg-slate-200 flex-1" />
          <span className="text-xs text-slate-400">or</span>
          <div className="h-px bg-slate-200 flex-1" />
        </div>
        <Button variant="outline" onClick={googleLogin} data-testid="auth-google"
          className="w-full rounded-full py-6 border-2">
          Continue with Google
        </Button>

        <p className="text-center text-sm text-slate-500">
          {mode === "login" ? "New to Voyes?" : "Already have an account?"}{" "}
          <button type="button" data-testid="auth-toggle-mode"
            onClick={() => setMode(mode === "login" ? "register" : "login")}
            className="text-orange-600 font-semibold">
            {mode === "login" ? "Sign up" : "Sign in"}
          </button>
        </p>
        {mode === "register" && (
          <p className="text-center text-xs text-slate-400 leading-relaxed">
            By creating an account you agree to our{" "}
            <Link to="/terms" onClick={onClose} className="text-orange-600 hover:underline">Terms of Service</Link> and{" "}
            <Link to="/privacy" onClick={onClose} className="text-orange-600 hover:underline">Privacy Policy</Link>.
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
